import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useStore } from "react-redux";
import { useAuth } from "./AuthProvider";
import { mainActions } from "../store";
import { exportChartDocument } from "../store/personalData";
import { chartErrorMessage, createChart, getChart, updateChart, deleteChart, moveChart } from "./chartRepository";

export const SavedChartsContext = createContext({ configured: false, busy: false, active: null });
export const useSavedCharts = () => useContext(SavedChartsContext);
export const chartFingerprint = state => JSON.stringify(exportChartDocument(state));

const SavedChartsProvider = ({ children }) => {
  const auth = useAuth();
  const store = useStore();
  const userId = auth.session?.user.id;
  const authRef = useRef(auth);
  authRef.current = auth;
  const pending = useRef(null);
  const [active, setActive] = useState(null);
  const [libraryVersion, setLibraryVersion] = useState(0);
  const [status, setStatus] = useState({ userId, busy: false, error: "", message: "" });
  const activeRef = useRef(active);
  activeRef.current = active;
  useEffect(() => {
    setActive(null);
    setStatus({ userId, busy: false, error: "", message: "" });
    return () => { pending.current?.controller.abort(); pending.current = null; };
  }, [userId]);

  const run = async (operation, signal) => {
    const current = authRef.current;
    const owner = current.session?.user.id;
    if (!owner || !current.client || current.loading || current.error) return null;
    if (pending.current?.userId === owner) return null;
    pending.current?.controller.abort();
    const job = { userId: owner, controller: new AbortController() };
    pending.current = job;
    const abort = () => job.controller.abort();
    signal?.addEventListener("abort", abort, { once: true });
    if (signal?.aborted) abort();
    const valid = () => pending.current === job && !job.controller.signal.aborted && authRef.current.session?.user.id === owner;
    setStatus({ userId: owner, busy: true, error: "", message: "" });
    try {
      if (!valid()) return null;
      const result = await operation({ client: current.client, accessToken: current.session.access_token,
        signal: job.controller.signal }, owner, valid);
      return valid() ? result : null;
    } catch (error) {
      if (valid()) setStatus({ userId: owner, busy: false, error: chartErrorMessage(error), message: "" });
      return null;
    } finally {
      signal?.removeEventListener("abort", abort);
      if (pending.current === job) {
        pending.current = null;
        setStatus(previous => previous.userId === owner ? { ...previous, busy: false } : previous);
      }
    }
  };

  const save = copy => run(async (context, owner, valid) => {
    const document = exportChartDocument(store.getState());
    const previous = activeRef.current?.userId === owner ? activeRef.current : null;
    const result = previous && !copy ? await updateChart(context, previous.id, previous.revision, document)
      : await createChart(context, document);
    if (valid()) {
      setActive({ id: result.id, revision: result.revision, userId: owner, baseline: JSON.stringify(document) });
      setLibraryVersion(value => value + 1);
      setStatus({ userId: owner, busy: false, error: "", message: "Saved to My Charts." });
    }
    return result;
  });

  const open = (id, signal) => run(async (context, owner, valid) => {
    const result = await getChart(context, id);
    if (!valid()) return null;
    const previous = activeRef.current?.userId === owner ? activeRef.current : null;
    const state = store.getState();
    const dirty = previous ? chartFingerprint(state) !== previous.baseline
      : state.refineData.perChartData.length > 0 || state.introduction !== "" || state.title !== "Put your chart name here...";
    if (dirty && !window.confirm("Open this saved chart and replace your unsaved chart? Your current changes will be lost.")) return null;
    if (!valid()) return null;
    store.dispatch(mainActions.loadChartDocument(result.document));
    setActive({ id: result.id, revision: result.revision, userId: owner, baseline: JSON.stringify(result.document) });
    setStatus({ userId: owner, busy: false, error: "", message: "" });
    return result;
  }, signal);

  const changeSaved = (kind, item, signal, path = "") => run(async (context, owner, valid) => {
    if (kind === "move") {
      const result = await moveChart(context, item.id, item.revision, path);
      if (valid() && activeRef.current?.id === item.id && activeRef.current.revision === item.revision) {
        setActive(previous => ({ ...previous, revision: result.revision }));
      }
    } else if (kind === "delete") {
      await deleteChart(context, item.id, item.revision);
      if (valid() && activeRef.current?.id === item.id) setActive(null);
    } else {
      const source = await getChart(context, item.id);
      if (source.revision !== item.revision) throw Object.assign(new Error("Changed"), { code: "PT409" });
      if (!valid()) return null;
      source.document.chart.title = `${source.document.chart.title.slice(0, 993)} (copy)`;
      await createChart(context, source.document);
    }
    if (valid()) {
      setLibraryVersion(value => value + 1);
      setStatus({ userId: owner, busy: false, error: "",
        message: kind === "delete" ? "Saved chart deleted. Any open draft has been kept." : kind === "move" ? "Library location saved." : "Chart duplicated." });
    }
    return true;
  }, signal);

  const newChart = () => {
    pending.current?.controller.abort();
    pending.current = null;
    setActive(null);
    setStatus({ userId, busy: false, error: "", message: "" });
    store.dispatch(mainActions.startNewChart());
  };

  const visibleStatus = status.userId === userId ? status : { busy: false, error: "", message: "" };
  return <SavedChartsContext.Provider value={{ configured: auth.configured, ready: !!userId && !!auth.client && !auth.loading && !auth.error,
    ...visibleStatus, active: active?.userId === userId ? active : null, save, open, changeSaved, libraryVersion, newChart }}>
    {children}
  </SavedChartsContext.Provider>;
};

export default SavedChartsProvider;
